/**
 * "PO format" under the PO rule on a customer's Settings (docs/features/
 * credit-control/18_po_format_implementation_summary.md): what this customer's
 * PO numbers look like, as a template finance types (`45########`), and a
 * "Test a PO number" box.
 *
 * Neither the template's grammar nor the rule that refuses "TBC" lives here.
 * The hint, the reading back ("45 then 8 digits, like 4500000000") and every
 * verdict come from the backend: the test box posts the draft rule, format and
 * number to credit control's check route and shows its answer. A save the
 * backend refuses on the template binds its message to the input (`serverMessage`).
 */
import { useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CustomerCreditView,
  PoNumberCheck,
  PoRule,
} from "@shared-core/data/credit-control-types";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";

const MESSAGE_ID = "credit-po-format-message";
const HINT_ID = "credit-po-format-hint";

/** What the page already knows about the saved format, to read back while the draft is unchanged. */
export interface SavedPoFormat {
  format: string;
  label: string | null;
  example: string | null;
}

interface TestOutcome {
  /** What was tested, so an answer is shown only while the edits still match it. */
  key: string;
  /** The template the answer is about. */
  format: string;
  check: PoNumberCheck | null;
  failure: string | null;
}

export function PoFormatField({
  view,
  poRule,
  value,
  onChange,
  disabled,
  saved,
  serverMessage,
}: {
  view: Pick<CustomerCreditView, "poFormatHint" | "poFormatCheckUrl">;
  poRule: PoRule;
  /** The template as typed; empty is none. */
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  saved: SavedPoFormat | null;
  /** Why a save was refused on the template, when it was. */
  serverMessage?: string;
}) {
  const adapter = useDataAdapter();
  const [poNumber, setPoNumber] = useState("");
  const [testing, setTesting] = useState(false);
  const [outcome, setOutcome] = useState<TestOutcome | null>(null);

  const testKey = `${poRule}|${value}|${poNumber}`;
  const shown = outcome?.key === testKey ? outcome : null;
  const templateRefused = shown?.check?.field === "po_format";
  const message =
    serverMessage ?? (templateRefused ? shown?.check?.message : null);

  // The reading of the template: from the latest answer for this very template, else the saved one while untouched.
  const reading =
    outcome?.check?.poFormatLabel && outcome.format === value
      ? {
          label: outcome.check.poFormatLabel,
          example: outcome.check.poFormatExample,
        }
      : saved && saved.format === value
        ? { label: saved.label, example: saved.example }
        : null;

  const test = async () => {
    if (!poNumber.trim() || testing) return;
    setTesting(true);
    try {
      const check = await adapter.checkPoNumber(view, {
        poRule,
        poFormat: value === "" ? null : value,
        value: poNumber,
      });
      setOutcome({ key: testKey, format: value, check, failure: null });
    } catch (err) {
      setOutcome({
        key: testKey,
        format: value,
        check: null,
        failure: mutationErrorMessage(err, "Could not test that PO number."),
      });
    } finally {
      setTesting(false);
    }
  };

  const describedBy =
    [message ? MESSAGE_ID : null, HINT_ID].filter(Boolean).join(" ") ||
    undefined;

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor="credit-po-format" className="font-medium">
          PO format
        </Label>
        <Input
          id="credit-po-format"
          className="w-56 font-mono"
          value={value}
          placeholder="45########"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={message ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
        />
        {reading?.label && (
          <span className="text-xs text-muted-foreground">
            {reading.label}
            {reading.example ? `, like ${reading.example}` : ""}
          </span>
        )}
      </div>
      <p id={HINT_ID} className="text-xs text-muted-foreground">
        {view.poFormatHint}
      </p>
      {message && (
        <p id={MESSAGE_ID} role="alert" className="text-xs text-destructive">
          {message}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor="credit-po-format-test" className="font-medium">
          Test a PO number
        </Label>
        <Input
          id="credit-po-format-test"
          className="w-56"
          value={poNumber}
          autoComplete="off"
          onChange={(e) => setPoNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void test();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!poNumber.trim() || testing}
          onClick={() => void test()}
        >
          Test
        </Button>
      </div>
      <div role="status" aria-live="polite" className="min-h-4 text-xs">
        {shown?.failure && (
          <span className="text-destructive">{shown.failure}</span>
        )}
        {shown?.check &&
          !templateRefused &&
          (shown.check.isAcceptable ? (
            <span className="text-green-700 dark:text-green-400">
              Accepted: this would be taken as a PO number.
            </span>
          ) : (
            <span className="text-destructive">{shown.check.message}</span>
          ))}
      </div>
    </fieldset>
  );
}
